import { projects } from '../data/content';
import { ProjectRow } from '../components/ProjectRow';
import { filterProjects } from '../lib/projects';
import { Ambulance, BusFront, CircleDollarSign, Factory, HeartPulse, Hospital, Landmark, MapPinned, Route, Signpost, Tractor, Wind } from 'lucide-react';
import { AreaHero } from '../components/AreaHero';
import { AreaScope } from '../components/AreaScope';
import { AreaNavigator } from '../components/AreaNavigator';
import { areaQuantity, areaValue, projectValue } from '../lib/transparency';
import { formatSummaryCurrency } from '../lib/format';

interface AreaPageProps {
  area: 'saude' | 'infraestrutura' | 'grandes-obras' | 'investimentos';
}

const content = {
  saude: {
    title: 'Mais estrutura para', accent: 'cuidar das pessoas.',
    lead: 'Unidades Básicas de Saúde e veículos apresentados para a atenção básica e o atendimento em Palmas.',
    meta: '',
    stats: [{ icon: Hospital, value: areaQuantity(projects, 'saude', 'units'), label: 'futuras UBS' }, { icon: Ambulance, value: areaQuantity(projects, 'saude', 'vehicles'), label: 'veículos' }, { icon: HeartPulse, value: formatSummaryCurrency(areaValue(projects, 'saude').value), label: 'recursos públicos' }],
    heading: 'Cuidado que começa perto de casa.',
    intro: 'A frente reúne estrutura física e mobilidade para ampliar o alcance dos serviços de saúde no município.',
    topics: [
      { title: 'Atenção básica', description: `${projects.find((project) => project.slug === 'ubs')!.scope} apresentadas.` },
      { title: 'Transporte de pacientes', description: projects.find((project) => project.slug === 'veiculos-saude')!.scope! },
    ],
  },
  infraestrutura: {
    title: 'Mobilidade para', accent: 'a cidade e o interior.',
    lead: 'Pavimentação urbana, estradas rurais e equipamentos organizados por escopo, valor e estágio.',
    meta: '',
    stats: [{ icon: Route, value: areaQuantity(projects, 'infraestrutura', 'streets'), label: 'ruas apresentadas' }, { icon: MapPinned, value: `${areaQuantity(projects, 'infraestrutura', 'roadLengthMeters')} m`, label: 'no interior' }, { icon: Tractor, value: areaQuantity(projects, 'infraestrutura', 'machines'), label: 'máquinas e caminhões' }],
    heading: 'Da rua do bairro à estrada rural.',
    intro: 'As iniciativas conectam pavimentação, manutenção e capacidade operacional para melhorar deslocamentos e serviços.',
    topics: [
      { title: 'Pavimentação urbana', description: `${projects.find((project) => project.slug === 'asfalto-pedras')!.scope} sobre pedras irregulares e ${projects.find((project) => project.slug === 'asfalto-natural')!.scope} de leito natural.` },
      { title: 'Conexão com o interior', description: `${projects.find((project) => project.slug === 'asfalto-rural')!.scope} de pavimentação na Estrada Fundo Grande.` },
      { title: 'Capacidade de manutenção', description: projects.find((project) => project.slug === 'maquinas-e-equipamentos')!.scope! },
    ],
  },
  'grandes-obras': {
    title: 'Estrutura para hoje.', accent: 'Desenvolvimento para o futuro.',
    lead: 'Projetos estruturantes apresentados para turismo, serviços, acessibilidade e desenvolvimento econômico.',
    meta: '',
    stats: [{ icon: Landmark, value: '3', label: 'projetos apresentados' }, { icon: BusFront, value: '1', label: 'reforma de terminal' }, { icon: Signpost, value: formatSummaryCurrency(areaValue(projects, 'grandes-obras').value), label: 'recursos públicos e contrapartida' }],
    heading: 'Espaços que recebem, conectam e representam Palmas.',
    intro: 'Três projetos articulam eventos, mobilidade intermunicipal e a identidade visual das entradas da cidade.',
    topics: [
      { title: 'Eventos e desenvolvimento', description: 'O Centro de Eventos Maria José Piana é apresentado como espaço para feiras, cultura, encontros e negócios.' },
      { title: 'Mobilidade e acessibilidade', description: 'A reforma do Terminal Rodoviário prevê modernização e requalificação dos espaços de atendimento.' },
      { title: 'Portais de entrada', description: 'Os novos portais são apresentados como marcos de recepção e identidade urbana de Palmas.' },
    ],
  },
  investimentos: {
    title: 'Investimentos com', accent: 'origem e estágio claros.',
    lead: 'Recursos públicos e investimento privado aparecem separadamente, com o estágio de cada iniciativa.',
    meta: '',
    stats: [{ icon: CircleDollarSign, value: String(projects.length), label: 'iniciativas organizadas' }, { icon: Wind, value: projectValue(projects.find((project) => project.slug === 'eolico')!), label: 'investimento privado' }, { icon: Factory, value: '4', label: 'frentes de atuação' }],
    heading: 'Cada valor no seu contexto.',
    intro: 'Esta visão reúne todas as frentes e distingue recurso público, contrapartida e investimento privado.',
    topics: [
      { title: 'Recursos públicos', description: 'Saúde, pavimentação, equipamentos e grandes obras aparecem com valor, escopo e estágio de cada projeto.' },
      { title: 'Contrapartida', description: 'Valores dessa natureza são identificados separadamente para evitar a soma de origens diferentes.' },
      { title: 'Investimento privado', description: 'O Complexo Eólico Palmas II é apresentado fora do total de recursos públicos.' },
    ],
  },
};

export function AreaPage({ area }: AreaPageProps) {
  const copy = content[area];
  const entries = area === 'investimentos' ? filterProjects(projects) : filterProjects(projects, area);
  const heroMedia = entries.find((project) => project.featured)?.media[0] ?? entries[0].media[0];
  return <div className={`area-page area-page--${area}`}>
    <AreaHero title={copy.title} accent={copy.accent} lead={copy.lead} meta={copy.meta} media={heroMedia} stats={copy.stats} />
    <AreaScope heading={copy.heading} intro={copy.intro} topics={copy.topics} />
    <section className="project-list shell" aria-label="Projetos desta frente">
      <div className="project-list__heading"><h2>Projetos e registros</h2><p>Selecione um projeto para conferir escopo, valores e situação.</p></div>
      {entries.map((project, index) => <ProjectRow key={project.slug} project={project} reverse={index % 2 === 1} />)}
    </section>
    <AreaNavigator area={area} />
  </div>;
}
